// Hallens avbokningsvillkor, formaterade FÖRE köp.
//
// Kunden ska veta vad som gäller innan hen betalar, inte efter. Raden VISAR
// bara — avgiftsmotorn (FEL-006, useMyBookings.ts:197) ligger i G8-spåret och
// rörs inte härifrån.
//
// Semantiken speglar `pickMatchingTier` i src/lib/cancellationFee.ts: tiers
// sorteras DESC på `hours_before`, och den första där `hoursLeft >= hours_before`
// gäller. Med AP:s/demons konfiguration
//   [{0 %, 24h}, {50 %, 6h}, {100 %, 0h}]
// betyder det: gratis fram till start−24h, 50 % fram till start−6h, sedan 100 %.
//
// Multi-tenant: allt läses ur hallens egen `settings.cancellation`. Ingenting
// hårdkodas — demo saknar t.ex. `no_show`-blocket som AP har.

export interface PolicyTier {
  hours_before: number;
  percent: number;
}

export interface CancellationPolicyInput {
  tiers: readonly PolicyTier[];
  /** Bokningens starttid — gränserna uttrycks relativt den. */
  startTime: Date;
  /** Hallens `settings.cancellation.no_show` — meningen visas bara när den är påslagen. */
  noShowEnabled?: boolean;
}

export interface PolicySegment {
  /** Avgift i procent inom segmentet. */
  percent: number;
  /** Segmentet gäller fram till denna tidpunkt; `null` = ända fram till start. */
  until: Date | null;
  /** Timmar före start då segmentet upphör. */
  hoursBefore: number;
}

/**
 * Tiers → segment i kronologisk ordning (tidigast först), med absoluta
 * brytpunkter räknade från `startTime`.
 *
 * Returnerar tom lista när ingen tier-konfiguration finns — anroparen ska då
 * inte rendera någon rad alls hellre än att gissa villkor.
 */
export function buildCancellationPolicySegments({
  tiers,
  startTime,
}: Pick<CancellationPolicyInput, "tiers" | "startTime">): PolicySegment[] {
  if (!tiers || tiers.length === 0) return [];

  // Samma sortering som pickMatchingTier: högst hours_before först.
  const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);

  return sorted.map((tier) => ({
    percent: tier.percent,
    hoursBefore: tier.hours_before,
    // hours_before = 0 gäller ända fram till start → ingen egen brytpunkt.
    until:
      tier.hours_before > 0
        ? new Date(startTime.getTime() - tier.hours_before * 60 * 60 * 1000)
        : null,
  }));
}

/**
 * Den fria avbokningsgränsen, om hallen har en (percent === 0).
 * `null` när ingen tier är avgiftsfri — då är avbokning alltid belagd.
 */
export function freeCancellationUntil(input: Pick<CancellationPolicyInput, "tiers" | "startTime">): Date | null {
  const free = buildCancellationPolicySegments(input).find((s) => s.percent === 0);
  return free ? free.until : null;
}
